'use client';

import Script from 'next/script';
import { useEffect } from 'react';
import { tracking } from '@/lib/tracking';

interface TrackingScriptsProps {
  gtmId?: string;
  redditPixelId?: string;
  xPixelId?: string;
  linkedInPartnerId?: string;
}

/**
 * Tracking Scripts Component
 * Loads GTM, Reddit Pixel, X Pixel, and LinkedIn Insight Tag with proper initialization
 */
export function TrackingScripts({ gtmId, redditPixelId, xPixelId, linkedInPartnerId }: TrackingScriptsProps) {
  useEffect(() => {
    // Initialize tracking services once scripts are loaded
    tracking.init({
      gtmId,
      redditPixelId,
      xPixelId,
      linkedInPartnerId,
    });
  }, [gtmId, redditPixelId, xPixelId, linkedInPartnerId]);

  return (
    <>
      {/* Google Tag Manager */}
      {gtmId && (
        <>
          <Script
            id="gtm-script"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${gtmId}');
              `,
            }}
          />
        </>
      )}

      {/* Reddit Pixel */}
      {redditPixelId && (
        <Script
          id="reddit-pixel"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              !function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);
            `,
          }}
        />
      )}

      {/* X (Twitter) Pixel */}
      {xPixelId && (
        <Script
          id="x-pixel"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              !function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);
              },s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',
              a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');
              twq('config','${xPixelId}');
            `,
          }}
        />
      )}

      {/* LinkedIn Insight Tag */}
      {linkedInPartnerId && (
        <>
          <Script
            id="linkedin-insight-tag"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                _linkedin_partner_id = "${linkedInPartnerId}";
                window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
                window._linkedin_data_partner_ids.push(_linkedin_partner_id);
              `,
            }}
          />
          <Script
            id="linkedin-insight-script"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                (function(l) {
                  if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
                  window.lintrk.q=[]}
                  var s = document.getElementsByTagName("script")[0];
                  var b = document.createElement("script");
                  b.type = "text/javascript";b.async = true;
                  b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
                  s.parentNode.insertBefore(b, s);
                })(window.lintrk);
              `,
            }}
          />
        </>
      )}
    </>
  );
}

/**
 * No-JS pixel fallbacks (GTM iframe + LinkedIn img).
 *
 * MUST render inside <body>, never <head>: with scripting enabled the HTML
 * parser only allows link/style/meta inside a head-level <noscript> — an
 * <img>/<iframe> there force-closes the head, re-parents every following
 * head tag into <body>, and the resulting document-level hydration mismatch
 * killed React app-wide (P0, 2026-08-12). GTM's and LinkedIn's own install
 * docs specify the noscript snippet goes right after the opening <body> tag.
 */
export function TrackingNoscripts({
  gtmId,
  linkedInPartnerId,
}: Pick<TrackingScriptsProps, 'gtmId' | 'linkedInPartnerId'>) {
  return (
    <>
      {gtmId && (
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          />
        </noscript>
      )}
      {linkedInPartnerId && (
        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: 'none' }}
            alt=""
            src={`https://px.ads.linkedin.com/collect/?pid=${linkedInPartnerId}&fmt=gif`}
          />
        </noscript>
      )}
    </>
  );
}
